<!--

============================================================================================================
||    TagInput component                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component enables user to input tags. Each tag can be edited, removed and marked as invalid.

|   Features
------------------------------------------------------------------------------------------------------------
  - tags list
  - tags with condition in between

|   Props
------------------------------------------------------------------------------------------------------------
  - value         : Object    : Used for two-way binding.
  - basic         : Boolean   : If true, component is rendered as transparent with no borders or any other styling.
  - condition     : String    : Condition between tags.
  - showCondition : Boolean  : If true, condition is shown between tags.
  - singleTag     : Boolean  : If true, only one tag can be added. Every new tag will replace the previous one.

|   Events
------------------------------------------------------------------------------------------------------------
  - input       : Object    : Emits object with tags, condition and valid parameters.

|   v-model
------------------------------------------------------------------------------------------------------------
  - valid      : Boolean   : If true, all tags are valid.
  - tags       : Array     : List of tag objects.
  - condition  : String    : Condition between tags.

|   TODO
------------------------------------------------------------------------------------------------------------
  - builtin validators
    - duplicate (local and remote)
    - email
  - custom validator - e.g. pass method to check if all tags start with uppercase
  - split by character as option - e.g. split by comma -> 'tag1, tag2, tag3' -> ['tag1', 'tag2', 'tag3']
  - allowDuplicates prop
  - custom duplicate and invalid tooltips
  - allowEmpty prop
  - ON REWRITE check InviteTeamMembers modal which contains original logic to check everything once more ond join into one component
  - add on blur as an option
  - edit tag on click and position cursor correctly
  - set color for duplicate once validator is created since invalid will probably be red.

-->

<template>
  <multiselect
    ref="tagInputRef"
    v-model="tags"
    class="tag-input"
    tag-placeholder=""
    :placeholder="placeholder"
    track-by="id"
    :class="{ 'tags-selected': tags.length, basic, 'single-line': singleLine }"
    :options="[]"
    :multiple="true"
    :taggable="true"
    :close-on-select="false"
    @tag="addTag"
    @close="addTagOnBlur"
    @open="initialInputWidth"
    @search-change="tagSearchChange"
    @remove="removeTag"
    @click.native.stop="activate" >
    <template
      slot="tag"
      slot-scope="{ option, remove }" >
      <span
        :id="`tag-pill-${option ? option.id : ''}`"
        aria-hidden="true"
        tabindex="1"
        class="multiselect__tag pill"
        :class="[option && option.duplicate ? 'pill-yellow' : option && option.valid ? '' : 'pill-red shake', { shake: option && option.shake }]"
        @click="editTag(option)"
        @mousedown.prevent >
        <span
          :ref="`tag-${option ? option.id : ''}`"
          class="ellipsis"
          contenteditable="plaintext-only"
          @keydown="catchEnter"
          @focusout="proccessTag" >{{ option ? option.tag : '' }}</span>
        <SBIcon
          :ref="`remove-btn-${option.id}`"
          class="pill-remove"
          :icon="SBIconList.CLOSE_THIN"
          :color="option && option.duplicate ? '#B58E11' : option && option.valid ? SBColors.blackUltralight : SBColors.redPrimary"
          :size="7"
          :hover-size="14"
          @click.native.stop="remove(option)" />
      </span>
      <span
        v-if="option && showCondition && tags.slice(-1)[0].id !== option.id"
        class="condition"
        @click="toggleCondition"
        @mousedown.prevent >{{ conditionLocal }}</span>
      <b-popover
        v-if="option && (option.duplicate || !option.valid)"
        :target="`tag-pill-${option.id}`"
        boundary="window"
        triggers="hover"
        placement="top"
        custom-class="basic-popover" >
        {{ option && option.duplicate ? 'Duplicate keyword' : 'Invalid keyword' }}
      </b-popover>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import randomString from '@/helpers/randomString';

export default {
  name: 'TagInput',
  components: {
    Multiselect,
    SBIcon,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    basic: {
      type: Boolean,
      default: false,
    },
    conditions: {
      type: Array,
      default: () => ['and', 'or'],
    },
    condition: {
      type: String,
      default: 'and',
    },
    showCondition: {
      type: Boolean,
      default: false,
    },
    singleTag: {
      type: Boolean,
      default: false,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tags: [],
      conditionLocal: '',
      editingTag: null,
      lastTagSearch: '',
      changed: false,
      SBIconList,
      SBColors,
      tagInFocus: null,
    };
  },
  computed: {
    placeholder() {
      return this.tags.length ? '' : (this.singleTag ? 'Enter a single keyword' : 'Enter keywords');
    },
  },
  watch: {
    tags() {
      this.emitChanges();
    },
    value(newValue, oldValue) {
      if (!this.$lodash.isEqual(newValue, oldValue)) {
        this.setInitialValues();
      }
    },
    singleTag() {
      if (this.tags && this.tags.length > 1) {
        this.tags.length = 1;
      }
      this.emitChanges();
    },
  },
  mounted() {
    this.setInitialValues();
    window.addEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    handleKeyDown(event) {
      switch (event.keyCode) {
        // LEFT arrow
        case 37:
          if (this.tagInFocus === 1 || (this.tagInFocus && window.getSelection().anchorOffset > 0)) {
            return;
          }
          if (!this.tagInFocus) {
            this.tagInFocus = this.tags.length;
          } else {
            this.tagInFocus -= 1;
          }
          this.tagInFocus = Math.max(1, this.tagInFocus);
          this.editTag(this.tags[this.tagInFocus - 1]);
          break;
        // RIGHT arrow
        case 39:
          if (this.tagInFocus && window.getSelection().anchorOffset < this.tags[this.tagInFocus - 1].tag.length) {
            return;
          }
          if (this.tagInFocus) {
            this.tagInFocus += 1;
          }
          this.tagInFocus = this.tagInFocus > this.tags.length ? null : this.tagInFocus;
          
          if (this.tagInFocus && this.tagInFocus <= this.tags.length) {
            this.tagInFocus = Math.max(1, this.tagInFocus);
            this.editTag(this.tags[this.tagInFocus - 1], true);
          } else {
            this.focus();
          }
          break;
        default:
          break;
      }
    },
    focus() {
      this.$refs.tagInputRef.$el.focus();
    },
    setInitialValues() {
      if (this.condition) {
        this.conditionLocal = this.value ? this.value.condition || this.condition : this.condition;
      }
      if (this.value && this.value.tags) {
        this.tags = this.value.tags.map(tag => ({
          tag: tag.tag,
          id: tag.id ? tag.id : randomString(),
          valid: true,
        }));
      }
      this.tags.forEach((tag) => {
        this.markDuplicate(tag.tag);
      });
      this.emitChanges();
    },
    /**
     * Method to activate the component. It is used to trigger validation and height recalculation on components that use this component.
     */
    activate() {
      // to trigger validation and height recalculation on components that use this component
      this.emitChanges();
    },
    addTag(tag) {
      if (!(tag.trim())) return; // do not add if tag is empty string
      const tagObject = {
        tag: tag.trim(),
        valid: true,
        id: randomString(),
      };
      if (this.singleTag) {
        this.tags = [tagObject];
      } else {
        this.tags.push(tagObject);
        this.markDuplicate(tag);
      }
      this.lastTagSearch = '';
      this.resetInputWidth('10');
      this.multiselectScrollLeft();
    },
    addTagOnBlur() {
      this.changed = true;
      this.addTag(this.lastTagSearch);
      this.$refs.tagInputRef.$el.blur();
      // to trigger validation and height recalculation on components that use this component
      this.emitChanges();
      // if (this.lastTagSearch) {
      //   this.parseAndAddEmails(this.lastEmailSearch);
      // }
      this.resetInputWidth();
    },
    multiselectScrollLeft() {
      if (this.singleLine) {
        const input = this.$refs.tagInputRef.$el.getElementsByClassName('multiselect__tags');
        if (input && input.length) {
          setTimeout(() => {
            input[0].scrollLeft = 10000;
          }, 0);
        }
      }
    },
    resetInputWidth(newWidth = 0) {
      if (this.singleLine) {
        const input = this.$refs.tagInputRef.$el.getElementsByClassName('multiselect__input');
        if (input && input.length) {
          input[0].style.setProperty('min-width', `${newWidth}px`, 'important');
        }
      }
    },
    setInputWidth() {
      if (this.singleLine) {
        const input = this.$refs.tagInputRef.$el.getElementsByClassName('multiselect__input');
        if (input && input.length) {
          input[0].style.setProperty('min-width', `${input[0].scrollWidth + 10}px`, 'important');
        }
      }
    },
    initialInputWidth() {
      this.resetInputWidth(10);
    },
    tagSearchChange(searchQuery) {
      this.lastTagSearch = searchQuery;
      if (this.lastTagSearch.trim()) {
        this.setInputWidth();
      }
    },
    removeTag(tag) {
      const duplicateTagsReset = this.tags.filter(t => t.tag === tag.tag && t.duplicate);
      duplicateTagsReset.forEach((t) => { t.duplicate = false; });
      this.markDuplicate(tag.tag);
    },
    editTag(tag, focusStart = false) {
      if (this.editingTag) {
        // If one tag is in edit mode and we select another one than we should blur the previous email first and let it process. Then we can continue with editing the new one.
        this.$refs[`tag-${this.editingTag.id}`].blur();
        setTimeout(() => {
          this.editTag(tag, focusStart);
        }, 0);
        return;
      }
      this.editingTag = JSON.parse(JSON.stringify(tag));
      this.$refs[`tag-${tag.id}`].spellcheck = false;
      this.$refs[`tag-${tag.id}`].focus();
      const selection = document.getSelection();
      const node = this.$refs[`tag-${tag.id}`].firstChild;

      setTimeout(() => {
        if (!focusStart && selection.rangeCount) {
          ['Start', 'End'].forEach(pos => selection.getRangeAt(0)[`set${pos}`](node, node.length));
        }
      }, 0);
    },
    catchEnter(e) {
      if (e.keyCode === 13) {
        e.preventDefault();
        this.$refs[`tag-${this.editingTag.id}`].blur();
      }
    },
    proccessTag(e) {
      if (!this.editingTag) {
        return;
      }
      const newValue = e.target.innerText;
      const targetTagIndex = this.tags.findIndex(t => t.id === this.editingTag.id);

      if (targetTagIndex >= 0) {
        this.tags[targetTagIndex].tag = newValue;
      }

      this.editingTag = null;
      this.markDuplicate(newValue);
    },
    markDuplicate(tag) {
      const duplicateTags = this.tags.filter(t => t.tag && t.tag === tag); // empty tags will not be considered as duplicates. They should be handled by the parent component. Ignored or similar.
      if (duplicateTags.length > 1) {
        duplicateTags.forEach((t) => {
          t.duplicate = true;
          t.shake = true;
        });
      }
      this.tags = [...this.tags];
      setTimeout(() => {
        duplicateTags.forEach((t) => {
          t.shake = false;
        });
        this.tags = [...this.tags];
      }, 300);
    },
    // TODO
    isDuplicateRemote(email) {
      // this.fetchMembers({
      //   skipVuex: true,
      //   data: {
      //     search_query: email,
      //   },
      // }).then((members) => {
      //   if (members.length && members.some(member => member.email === email)) {
      //     const duplicateEmails = this.emails.filter(emailItem => emailItem.email === email);
      //     duplicateEmails.forEach((duplicateEmail) => {
      //       duplicateEmail.duplicate = true;
      //     });

      //     this.emails = [...this.emails];
      //   }
      // });
    },
    // TODO
    parseAndAddTags(bulkTags) {
    //   // parse out chunks using space or comma
    //   const parsedEmails = emailsString.split(/[\s,]/g);
    //   parsedEmails.forEach((email) => {
    //     if (email) { // skip empty string
    //       if (this.emails.findIndex(e => e.email.toLowerCase() === email.toLowerCase()) >= 0) {
    //         return;
    //       }
    //       const valid = this.isValidEmail(email);
    //       this.emails.push({
    //         email,
    //         valid,
    //         id: randomString(),
    //       });
    //       this.emails = [...this.emails];
    //       if (valid) {
    //         this.isDuplicate(email);
    //       }
    //     }
    //   });
    //   this.lastEmailSearch = '';
    },
    toggleCondition() {
      // conditions
      const currentDataOperatorIndex = this.conditions.findIndex(c => c === this.conditionLocal);
      if (currentDataOperatorIndex === this.conditions.length - 1) {
        this.conditionLocal = this.conditions[0];
      } else {
        this.conditionLocal = this.conditions[currentDataOperatorIndex + 1];
      }

      this.emitChanges();
    },
    emitChanges() {
      this.$emit('input', {
        tags: this.tags.map(tag => ({
          tag: tag.tag,
          id: tag.id,
        })),
        list: this.tags.map(tag => tag.tag),
        condition: this.conditionLocal,
        valid: !!this.tags.length && this.tags.every(tag => tag.valid && !tag.duplicate),
        changed: this.changed,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tag-input {
  // background-color: red;
  min-height: 32px !important;
  cursor: text;

  &.basic {
    min-height: 0px !important;
    box-shadow: none !important;

    ::v-deep {
      .multiselect__tags {
        background: transparent !important;
        padding: 0 !important;
        min-height: 0 !important;
        border: none !important;

        .multiselect__input {
          padding-bottom: 0 !important;
          background: transparent !important;
        }
      }
    }
  }

  ::v-deep {
    .multiselect__select {
      // width: 16px !important;
      display: none !important;
    }

    .multiselect__tags {
      // background-color: blue;
      display: block !important;
      padding-top: 6px !important;
      padding-bottom: 6px !important;
      padding-right: 10px !important;
      line-height: normal !important;
      min-height: 32px !important;

      .multiselect__tags-wrap {
        display: block !important;
        line-height: normal !important;
        overflow-wrap: anywhere;
        margin-bottom: -5px;

        .pill {
          display: inline-flex;
          align-items: center;
          cursor: default;
          border-radius: 9px;
          border: solid 1px $blackPrimary;
          background-color: $white;
          color: $blackPrimary;
          padding: 0 4px 0 7px;
          margin-right: 5px;

          span {
            font-size: 12px !important;
            font-weight: 500 !important;
            line-height: normal;
            white-space: nowrap;
          }

          .sb-icon:hover .icon {
            background-color: $blackPrimary !important;
          }

          &-blue {
            background: $lightBackgroundBlue;
            color: $bluePrimary;
          }

          &-red {
            background: $redUltraLight;
            color: $redPrimary;
          }

          &-yellow {
            background: #FAF1D2;
            color: #B58E11;

            color: #FF5B61;
            background: #FFEBEC;
            border-color: #FF5B61;

            .sb-icon .icon {
              background-color: #FF5B61 !important;
            }
            .sb-icon:hover .icon {
              background-color: #FF5B61 !important;
            }
          }

          b {
            font-weight: 700;
          }

          .pill-remove {
            cursor: pointer;
            margin-left: 3px;
          }
        }

        .condition {
          font-size: 12px;
          font-weight: normal;
          color: $blackPrimary;
          text-decoration-line: underline;
          text-decoration-style: dashed;
          margin-right: 5px;

          &:hover {
            cursor: pointer;
          }
        }
      }

      .multiselect__input {
        // top: -5px !important;
        top: 0 !important;
        margin-top: 5px;
        // bottom: 1px !important;
        left: 0 !important;
        min-height: 14px !important;
        line-height: normal !important;
        // padding-bottom: 5px;
        font-size: 14px !important;
        // display: block !important;
        // position: static !important;
      }

      .multiselect__placeholder {
        padding: 6px 0 0px !important;
        font-size: 14px !important;
        vertical-align: top !important;
      }
    }

    .multiselect__content-wrapper {
      display: none !important;
    }
  }

  &.single-line {
    border: 1px solid #CFD8DC;
    ::v-deep {
      .multiselect__tags {
        display: flex !important;
        gap: 0px !important;
        padding-top: 0px !important;
        padding-bottom: 15px !important;
        border: none !important;
        height: 50px;
        overflow-x: scroll;

        .multiselect__tags-wrap {
          display: flex !important;
          gap: 6px !important;
          align-items: center;

          .multiselect__tag * {
            flex-shrink: 0;
          }
          .multiselect__tag {
            margin: 0 0 4px 0 !important;
          }
          .condition {
            white-space: nowrap;
            margin-right: 0 !important;
            margin-bottom: 5px;
          }
        }

        input {
          margin-top: 0 !important;
          min-width: unset !important;
        }

        .multiselect__placeholder {
          padding-top: 0px !important;
          white-space: nowrap !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          line-height: normal !important;
        }
      }
    }

    &.multiselect--active {
      ::v-deep {
        input {
          min-width: 10px !important;
          width: auto !important;
        }
      }
    }
  }

  &.multiselect.tags-selected {
    ::v-deep .multiselect__input {
      margin-top: 5px;
      margin-left: 6px !important;
    }
  }
}
</style>
